import { type MantineSize } from '@mantine/core'
import { type ReactNode } from 'react'

export type FilterOption = {
  badgeSize?: MantineSize
  badgeColor?: string
  badgeWithDot?: boolean
  selected?: boolean
  icon?: ReactNode
  label: string
  value: string
  description?: string
  imageUrl?: string
}

export type DateRange = {
  startDate: string | null
  endDate: string | null
  startTime: string
  endTime: string
}

export type FilterField = {
  key: string
  icon?: ReactNode
  type:
    | 'booking-status'
    | 'payment-status'
    | 'deposit-status'
    | 'checkbox-list'
    | 'multiselect'
    | 'date-range'
    | 'customers'
    | 'products'
    | 'status'
    | 'text'
    | 'bookings'
    | 'date-time'
    | 'policy-status'
    | 'policy-type'
  label?: string
  value?: string
  placeholder?: string
  searchable?: boolean
  options?: FilterOption[]
  checkIconPosition?: 'left' | 'right'
}

export const CHECKBOX_GROUP_FIELDS = [
  'checkbox-list',
  'booking-status',
  'deposit-status',
  'payment-status',
  'policy-status',
  'policy-type',
]

export const MULTISELECT_QUERY_FIELD_TYPES = [
  'products',
  'customers',
  'bookings',
  'multiselect-query',
]

export type FilterValue = {
  key: string
  value: string | number | string[] | Date | DateRange
}

export const filterTypeConfig: Record<string, Omit<FilterField, 'key' | 'type'>> = {
  'booking-status': {
    options: [
      {
        label: 'options.bookingStatus.draft',
        value: 'drafting',
        badgeColor: 'gray',
      },
      {
        label: 'options.bookingStatus.quote',
        value: 'negotiating',
        badgeColor: 'purple',
      },
      {
        label: 'options.bookingStatus.reserved',
        value: 'reserved',
        badgeColor: 'blue',
      },
      {
        label: 'options.bookingStatus.ongoing',
        value: 'ongoing',
        badgeColor: 'orange',
      },
      {
        label: 'options.bookingStatus.complete',
        value: 'completed',
        badgeColor: 'green',
      },
      {
        label: 'options.bookingStatus.canceled',
        value: 'canceled',
        badgeColor: 'red',
      },
    ],
  },
  'deposit-status': {
    options: [
      {
        label: 'options.depositStatus.notCollected',
        value: 'payment_deposit_to_collect',
        badgeColor: 'red',
      },
      {
        label: 'options.depositStatus.collected',
        value: 'payment_deposit_collected',
        badgeColor: 'green',
      },
      {
        label: 'options.depositStatus.toReturn',
        value: 'payment_deposit_to_return',
        badgeColor: 'red',
      },
      {
        label: 'options.depositStatus.returned',
        value: 'payment_deposit_returned',
        badgeColor: 'red',
      },
      {
        label: 'options.depositStatus.noDeposit',
        value: 'payment_deposit_no_deposit',
        badgeColor: 'gray',
      },
    ],
  },
  'payment-status': {
    options: [
      {
        label: 'options.paymentStatus.notPaid',
        value: 'payment_rental_unpaid',
        badgeColor: 'red',
      },
      {
        label: 'options.paymentStatus.partiallyPaid',
        value: 'payment_rental_partially_paid',
        badgeColor: 'orange',
      },
      {
        label: 'options.paymentStatus.paid',
        value: 'payment_rental_paid',
        badgeColor: 'green',
      },
      {
        label: 'options.paymentStatus.overpaid',
        value: 'payment_rental_overpaid',
        badgeColor: 'red',
      },
    ],
  },
  'policy-status': {
    options: [
      {
        label: 'options.policyStatus.scheduled',
        value: 'scheduled',
        badgeColor: 'orange',
      },
      {
        label: 'options.policyStatus.active',
        value: 'active',
        badgeColor: 'green',
      },
      {
        label: 'options.policyStatus.within_claim_period',
        value: 'within_claim_period',
        badgeColor: 'blue',
      },
      {
        label: 'options.policyStatus.expired',
        value: 'expired',
        badgeColor: 'gray',
      },
      {
        label: 'options.policyStatus.canceled',
        value: 'canceled',
        badgeColor: 'red',
      },
    ],
  },
  'policy-type': {
    options: [
      {
        label: 'options.policyType.regular',
        value: 'regular',
        badgeColor: 'gray.7',
        badgeWithDot: false,
      },
      {
        label: 'options.policyType.earlyPickup',
        value: 'early_pickup',
        badgeColor: 'gray.7',
        badgeWithDot: false,
      },
      {
        label: 'options.policyType.extension',
        value: 'extension',
        badgeColor: 'gray.7',
        badgeWithDot: false,
      },
    ],
  },
}

export type HandleFilterChange = (key: string, value: string | string[]) => void
